import classNames from 'classnames';
import { FC, ReactNode, useId, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

import { Modal } from 'atoms/Modal';
import { Vertical } from 'atoms/layout/flex';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { useTransitionApi } from 'hooks/ui/useTransitionApi';
import { useBodyLock } from 'hooks/useBodyLock';
import { useShortcut } from 'hooks/useShortcut';
import { regularMaterial } from 'style/utils';

import { CloseButton } from './CloseButton';
import { Header } from './Header';

const openingAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
}`;

const GlobalStyle = createGlobalStyle<{
  dialogRootName: string;
  dialogContentName: string;
}>`
  /** Backdrop default fadeIn transition */
  ::view-transition-group(${({ dialogRootName }) => {
    return dialogRootName;
  }}) {
    animation-duration: 0.15s;
  }
  ::view-transition-old(${({ dialogRootName }) => {
    return dialogRootName;
  }}) {
    animation-delay: 0.1s;
  }
  /** Disable backdrop filter during transition */
  ::view-transition-group(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    backdrop-filter: none;
  }
  /** Opening animation */
  ::view-transition-new(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    animation: .2s ease-out ${openingAnimation} forwards;
  }
  /** Closing animation */
  ::view-transition-old(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    animation: .15s ease-out ${openingAnimation} reverse forwards;
  }
`;

const Root = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  overflow: auto;
  &:not(.fullScreen) {
    padding: var(--double-unit);
    background: var(--c-nl-900);
  }
`;
const Content = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden; /* For Safari <= v15. The sticky footer won't work there */
  overflow: clip;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  margin: auto;
  view-transition-name: dialog;
  ${regularMaterial}
  &.fullScreen {
    width: 100vw;
    height: 100%;
    border: 0;
    border-radius: 0;
    max-width: none;
    background: var(--c-black);
  }
`;
const BodyWrapper = styled.div`
  position: relative;
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;
const Footer = styled.footer`
  position: sticky;
  bottom: calc(-1 * var(--double-unit));
  margin-top: auto;
  padding: 0 var(--double-unit) var(--double-unit);
  &.withDivider {
    border-top: 1px solid var(--c-nd-150);
  }
`;

export type Props = {
  className?: string;
  children?:
    | FC<React.PropsWithChildren<{ CloseButton: typeof CloseButton }>>
    | ReactNode;
  title?: ReactNode;
  hideHeader?: boolean;
  footer?: ReactNode;
  open?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  fullWidth?: boolean;
  fullScreen?: boolean;
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false;
  disableEscapeKeyDown?: boolean;
  disableBackdropClick?: boolean;
  style?: React.CSSProperties;
  withDivider?: boolean;
};

const maxWidths = {
  xs: 444,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

export const Dialog = ({
  className,
  children,
  title,
  hideHeader,
  footer,
  open,
  onBack,
  onClose,
  fullWidth,
  fullScreen,
  maxWidth = 'xl',
  disableEscapeKeyDown,
  disableBackdropClick,
  style,
  withDivider,
}: Props) => {
  const { updateDOM } = useTransitionApi();
  useShortcut(
    'Escape',
    () => {
      if (open && !disableEscapeKeyDown) {
        onClose?.();
      }
    },
    [open]
  );
  const { up: isLaptop } = useScreenSize('laptop');
  const isFullScreen = fullScreen ?? !isLaptop;
  const [isOpened, setIsOpened] = useState(isFullScreen ? open : false);
  const dialogId = useId().replace(/:/g, '');
  const dialogRootName = `dialog-root-${dialogId}`;
  const dialogContentName = `dialog-content-${dialogId}`;
  const showHeader = !hideHeader && (onBack || title || onClose);

  useBodyLock(isOpened);

  if (isOpened !== open) {
    if (isFullScreen) {
      setIsOpened(open);
    } else {
      updateDOM(() => {
        setIsOpened(open);
      });
    }
  }

  return (
    <>
      <GlobalStyle
        dialogRootName={dialogRootName}
        dialogContentName={dialogContentName}
      />
      {!!isOpened && (
        <Modal>
          <Root
            className={classNames({
              fullScreen: isFullScreen,
            })}
            style={{
              viewTransitionName: dialogRootName,
            }}
            onClick={() => {
              if (!disableBackdropClick) {
                onClose?.();
              }
            }}
          >
            <Content
              role="dialog"
              className={classNames(className, {
                fullScreen: isFullScreen,
              })}
              style={{
                width: fullWidth ? '100%' : undefined,
                maxWidth:
                  maxWidth && !isFullScreen ? maxWidths[maxWidth] : undefined,
                viewTransitionName: dialogContentName,
                ...style,
              }}
              onMouseDown={e => e.stopPropagation()}
              onMouseUp={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            >
              {!!showHeader && (
                <Header
                  onBack={onBack}
                  title={title}
                  onClose={onClose}
                  withDivider={withDivider}
                />
              )}
              <BodyWrapper>
                {typeof children === 'function'
                  ? children?.({ CloseButton })
                  : children}
              </BodyWrapper>
              {!!footer && (
                <Footer className={classNames({ withDivider })}>
                  {footer}
                </Footer>
              )}
            </Content>
          </Root>
        </Modal>
      )}
    </>
  );
};
